<template>
  <div class="job-alerts-list">
    <data-table :service="JobAlertService"
                :entity="entity"
                :filters="filters"
                :columns="columns"
                operations="refresh,delete"
                :disablePermissionChecks="true"
                ref="table">
      <template v-slot:query="{row}">
        <div v-if="hasQuery(row)">
          <div v-if="row.query.search" class="d-flex align-items-center mb-1">
            <span class="mr-1 mb-1">{{$t('generic.search')}}:</span>
            <div>
              <badge type="info"
                     class="mr-1 mb-1 d-inline-flex">
                {{row.query.search}}
              </badge>
            </div>
          </div>
          <div v-if="row.query.segment_ids" class="d-flex align-items-center mb-1">
            <span v-if="get(row, 'query.segment_ids', []).length !== 0" class="mr-1 mb-1">{{$t('generic.segments')}}:</span>
            <div v-for="(segmentId, index) in limitSegments(row).segments"
                 :key="index"
                 class="job-alerts__segments-badge">
              <badge :key="index"
                     type="primary"
                     class="mr-1 mb-1 d-inline-flex segment-badge">
                <span class="text-white">{{getSegmentById(segmentId).value}}</span>
              </badge>
            </div>
            <badge v-if="limitSegments(row).total > maxSegmentsToDisplay"
                   type="primary"
                   class="mr-1 mb-1 d-inline-flex segment-badge">
                <span class="text-white">
                  + {{limitSegments(row).total - maxSegmentsToDisplay}} more
                </span>
            </badge>
          </div>
          <div v-if="get(row, 'query.location', '')" class="d-flex align-items-center mb-1">
            <span class="mr-1 mb-1">{{$t('organization.infoTypes.location')}}:</span>
            <div>
              <badge type="danger"
                     class="mr-1 mb-1 d-inline-flex">
                {{row.query.location}}
              </badge>
            </div>
          </div>
          <div v-if="row.query.range" class="d-flex align-items-center mb-1">
            <span class="mr-1 mb-1">{{$t('generic.range')}}:</span>
            <div>
              <badge type="success"
                     class="mr-1 mb-1 d-inline-flex">
                {{row.query.range}} {{distanceSymbol}}
              </badge>
            </div>
          </div>
        </div>
        <template v-else>
          {{$t('- -')}}
        </template>
      </template>
      <template v-slot:email="{row}">
        {{get(row, 'email', '- -')}}
      </template>
      <template v-slot:link="{row}">
        <a v-if="getAlertId(row.job_board_id, row.uuid)"
           @click.stop=""
           :href="getAlertId(row.job_board_id, row.uuid)"
           class="flex items-center justify-start w-full"
           target="_blank" rel="noopener">
          <external-link-icon class="mr-1"></external-link-icon>
          <span class="pt-1">{{$t('alerts.seeAlert')}}</span>
        </a>
      </template>
      <template v-slot:active="{row}">
        <status-badge :status="row.active ? 'active': 'inactive'"></status-badge>
      </template>
      <template v-slot:extra-filter-buttons="{lastQuery}">
        <base-dropdown position="right">
          <base-button slot="title"
                       icon
                       outline
                       type="primary">
            <i class="nc-icon nc-settings-gear-65"></i>
          </base-button>
          <h6 class="dropdown-header">{{$t('generic.settings')}}</h6>
          <div class="dropdown-item" @click="exportAlerts(lastQuery)">
            {{$t('generic.exportToCSV')}}
          </div>
          <div class="dropdown-item" @click="exportAlerts(lastQuery, true)">
            {{$t('generic.exportToCSVAll')}}
          </div>
        </base-dropdown>
      </template>
      <template v-slot:created_at="{row}">
        {{$formatDate(row.created_at)}}
      </template>
      <template v-slot:updated_at="{row}">
        {{$formatDate(row.updated_at)}}
      </template>
    </data-table>
  </div>
</template>
<script>
  import OrderService from '@/services/OrderService'
  import cloneDeep from 'lodash/cloneDeep'
  import { ExternalLinkIcon } from 'vue-feather-icons'
  import get from 'lodash/get'
  import Badge from 'src/components/Badge'
  import JobAlertService from 'src/services/JobAlertService';
  import JobFilters from 'src/views/admin/jobs/JobFilters';
  import entities from 'src/config/entities';

  export default {
    name: 'job-board-list',
    components: {
      ExternalLinkIcon,
      JobFilters,
      Badge,
    },
    props: {
      filters: {
        type: Array,
        default: () => [
          {
            key: 'with',
            value: 'candidate'
          },
          {
            key: 'sort',
            value: '+created_at'
          }
        ]
      }
    },
    data() {
      return {
        JobAlertService,
        entity: entities.JOB_ALERT,
        maxSegmentsToDisplay: 2
      }
    },
    computed: {
      jobBoards() {
        return this.$store.state.config.jobBoards
      },
      distanceSymbol() {
        return this.$store.getters['config/distanceSymbol']
      },
      columns() {
        return [
          {
            prop: "query",
            label: this.$t('jobAlerts.list.query'),
            minWidth: "400px"
          },
          {
            prop: "email",
            label: this.$t('generic.email'),
            minWidth: "200px"
          },
          {
            prop: "link",
            label: this.$t('jobBoard.list.labels.url'),
            minWidth: "200px"
          },
          {
            prop: "frequency",
            label: this.$t('jobAlerts.list.frequency'),
            minWidth: "150px",
            sortable: true
          },
          {
            prop: "active",
            label: this.$t('jobAlerts.list.active'),
            minWidth: "150px",
            sortable: true
          },
          {
            prop: "created_at",
            label: this.$t('generic.createdAt'),
            minWidth: "170px",
            sortable: true
          },
          {
            prop: "updated_at",
            label: this.$t('jobAlerts.list.updated_at'),
            minWidth: "150px",
            sortable: true
          }
        ]
      },
      getSegmentById() {
        return this.$store.getters['config/getSegmentById']
      },
    },
    methods: {
      get,
      getAlertId(id = '', jobAlertId = '') {
        const jobBoard = this.jobBoards.find(j => j.id.toString() === id.toString()) || { host: ''}
        if (!jobBoard.host) {
          return null
        }
        return `${jobBoard.host}/job-alert?uuid=${jobAlertId}`
      },
      async exportAlerts(query = {}, all = false) {
        let data = cloneDeep(query)
        if (all) {
          if (data.sort) {
            data.sort.limit = 999
          } else {
            data.sort = {
              limit: 9999
            }
          }
        }
        await JobAlertService.export(data)
      },
      hasQuery(row) {
        let query = get(row, 'query', null)
        let segments = get(row, 'query.segment_ids', [])
        let search = get(row, 'query.search', null)
        let location = get(row, 'query.location', null)
        let range = get(row, 'query.range', null)
        return query && (segments.length || search || location || range)
      },
      limitSegments(row) {
        const segmentGroups = get(row, 'query.segment_ids', [])
        const segmentIds = []
        segmentGroups.forEach(group => {
          if (!Array.isArray(group)) {
            segmentIds.push(parseInt(group))
            return
          }
          group.forEach(segmentId => {
            segmentIds.push(parseInt(segmentId))
          })
        })
        return {
          segments: segmentIds.slice(0, this.maxSegmentsToDisplay),
          total: segmentIds.length
        }
      }
    }
  }
</script>
<style lang="scss" scoped>
  .job-alerts__segments-badge {
    display: flex;
  }
  .segment-badge {
    min-width: 65px;
    text-align: center;
  }
</style>
